import axios from '@config/axios-config';
import { getPlatform } from '@config/environment';
import { IApiResponse, TAudioFormat } from '@datatype/common/api-type';
import {
  ISpeakingTaskData,
  ISpeakingSessionData,
  ISpeakingTurnData,
  ISpeakingTurnUploadData,
  ICreateSpeakingSessionRequest,
  IProblemData,
  IBundleData,
  IModifyProblemRequest,
  IStudentData,
  IModifyStudentRequest,
  ICheckAttendanceRequest,
  IAttendanceData,
  IReadAttendancePageRequest,
  IVocabularyData,
  IReadTodayClozeWorkbookRequest,
  IReadBookmarkClozeWorkbookRequest,
  IReadIncorrectNoteClozeWorkbookRequest,
  ICreateTodayTestWorkbookRequest,
  IWorkbookData,
  IModifyWorkbookRequest,
  IModifyWorksheetWorkbookRequest,
  IWorksheetWorkbookData,
  IWorksheetWorkbookUploadData,
  IWorksheetWorkbookTranscribeData,
  IStudyRecordData,
  IDailyStudyRecordData,
  IAuthenticExpressionData,
  IStoryData,
  EApiLanguageCode,
  EApiStoryType,
  IModifyVocabularyWorkbookRequest,
  IReadVocabularyBooksRequest,
  IReadVocabularyBookProgressRequest,
  IVocabularyBookProgress,
  IReadVocabularyWorkbookBookRequest,
  IReadVocabularyWorkbookBookmarkPageRequest,
  IReadVocabularyWorkbookIncorrectNotePageRequest,
  IReadReviewVocabularyWorkbookPageRequest,
  IVocabularyWorkbookData,
  IVocabularyBook,
  IClassroomCharacterGroupData,
  IClassroomCharacterGroupWorkbookData,
  IReadCharacterGroupWorkbooksRequest,
  IModifyCharacterGroupWorkbookRequest,
  EApiCharacterType,
  ICreateStudentRequest,
  IClassroomGrammarData,
  IClassroomGrammarWorkbookData,
  IReadClassroomGrammarWorkbooksRequest,
  IModifyClassroomGrammarWorkbookRequest,
  IClassroomExpressionData,
  IClassroomExpressionWorkbookData,
  IReadClassroomExpressionWorkbooksRequest,
  IModifyClassroomExpressionWorkbookRequest,
  IClassroomConversationData,
  IClassroomConversationWorkbookData,
  IReadClassroomConversationWorkbooksRequest,
  IModifyClassroomConversationWorkbookRequest,
  ICreateStudyRecordRequest,
} from '@datatype/iyagigo/api-type';

// ###################################
// character (일본어교실 입문 — 가나)
// ###################################
// 글자 학습 그룹 목록 조회 api — groupOrder 오름차순
// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// export const getCharacterGroups = async ({
//   languageCode,
//   characterType,
// }: {
//   languageCode: EApiLanguageCode;
//   characterType: EApiCharacterType;
// }): Promise<IApiResponse<IClassroomCharacterGroupData[]>> => {
//   const endpoint = `/api/v1/ig/classroom-character-group?languageCode=${languageCode}&characterType=${characterType}`;
//   return axios.get<IApiResponse<IClassroomCharacterGroupData[]>>(endpoint).then((res) => res.data);
// };

export const getCharacterGroupWorkbooks = async (
  request: IReadCharacterGroupWorkbooksRequest,
): Promise<IApiResponse<IClassroomCharacterGroupWorkbookData[]>> => {
  let endpoint = `/api/v1/ig/classroom-character-group/workbook?languageCode=${request.languageCode}&characterType=${request.characterType}`;
  if (request.studentKey) endpoint += `&studentKey=${request.studentKey}`;
  return axios.get<IApiResponse<IClassroomCharacterGroupWorkbookData[]>>(endpoint).then((res) => res.data);
};

export const modifyCharacterGroupWorkbook = async (
  request: IModifyCharacterGroupWorkbookRequest,
): Promise<IApiResponse<IClassroomCharacterGroupWorkbookData>> => {
  return axios
    .patch<IApiResponse<IClassroomCharacterGroupWorkbookData>>(
      `/api/v1/ig/classroom-character-group/workbook/${request.workbookKey}`,
      {
        isStudy: request.isStudy ?? null,
        isBookmark: request.isBookmark ?? null,
      },
    )
    .then((res) => res.data);
};

// ###################################
// classroom grammar (일본어교실 입문 — 기초문법)
// ###################################
// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// export const getClassroomGrammars = async ({
//   languageCode,
// }: {
//   languageCode: EApiLanguageCode;
// }): Promise<IApiResponse<IClassroomGrammarData[]>> => {
//   const endpoint = `/api/v1/ig/classroom-grammar?languageCode=${languageCode}`;
//   return axios.get<IApiResponse<IClassroomGrammarData[]>>(endpoint).then((res) => res.data);
// };

export const getClassroomGrammarWorkbooks = async (
  request: IReadClassroomGrammarWorkbooksRequest,
): Promise<IApiResponse<IClassroomGrammarWorkbookData[]>> => {
  let endpoint = `/api/v1/ig/classroom-grammar/workbook?languageCode=${request.languageCode}`;
  if (request.studentKey) endpoint += `&studentKey=${request.studentKey}`;
  return axios.get<IApiResponse<IClassroomGrammarWorkbookData[]>>(endpoint).then((res) => res.data);
};

export const modifyClassroomGrammarWorkbook = async (
  request: IModifyClassroomGrammarWorkbookRequest,
): Promise<IApiResponse<IClassroomGrammarWorkbookData>> => {
  return axios
    .patch<IApiResponse<IClassroomGrammarWorkbookData>>(
      `/api/v1/ig/classroom-grammar/workbook/${request.workbookKey}`,
      {
        isStudy: request.isStudy ?? null,
        isBookmark: request.isBookmark ?? null,
      },
    )
    .then((res) => res.data);
};

// ###################################
// basic expression (교실 기본표현)
// ###################################
// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// export const getClassroomExpressions = async ({
//   languageCode,
// }: {
//   languageCode: EApiLanguageCode;
// }): Promise<IApiResponse<IClassroomExpressionData[]>> => {
//   const endpoint = `/api/v1/ig/classroom-expression?languageCode=${languageCode}`;
//   return axios.get<IApiResponse<IClassroomExpressionData[]>>(endpoint).then((res) => res.data);
// };

export const getClassroomExpressionWorkbooks = async (
  request: IReadClassroomExpressionWorkbooksRequest,
): Promise<IApiResponse<IClassroomExpressionWorkbookData[]>> => {
  let endpoint = `/api/v1/ig/classroom-expression/workbook?languageCode=${request.languageCode}`;
  if (request.studentKey) endpoint += `&studentKey=${request.studentKey}`;
  return axios.get<IApiResponse<IClassroomExpressionWorkbookData[]>>(endpoint).then((res) => res.data);
};

export const modifyClassroomExpressionWorkbook = async (
  request: IModifyClassroomExpressionWorkbookRequest,
): Promise<IApiResponse<IClassroomExpressionWorkbookData>> => {
  return axios
    .patch<IApiResponse<IClassroomExpressionWorkbookData>>(
      `/api/v1/ig/classroom-expression/workbook/${request.workbookKey}`,
      {
        isStudy: request.isStudy ?? null,
        isBookmark: request.isBookmark ?? null,
      },
    )
    .then((res) => res.data);
};

// ###################################
// practical conversation (일본어교실 생활회화)
// ###################################
// [Deprecated] workbook 조회가 콘텐츠를 포함하므로 목록 조회는 쓰지 않는다.
// export const getClassroomConversations = async ({
//   languageCode,
// }: {
//   languageCode: EApiLanguageCode;
// }): Promise<IApiResponse<IClassroomConversationData[]>> => {
//   const endpoint = `/api/v1/ig/classroom-conversation?languageCode=${languageCode}`;
//   return axios.get<IApiResponse<IClassroomConversationData[]>>(endpoint).then((res) => res.data);
// };

export const getClassroomConversationWorkbooks = async (
  request: IReadClassroomConversationWorkbooksRequest,
): Promise<IApiResponse<IClassroomConversationWorkbookData[]>> => {
  let endpoint = `/api/v1/ig/classroom-conversation/workbook?languageCode=${request.languageCode}`;
  if (request.studentKey) endpoint += `&studentKey=${request.studentKey}`;
  return axios.get<IApiResponse<IClassroomConversationWorkbookData[]>>(endpoint).then((res) => res.data);
};

export const modifyClassroomConversationWorkbook = async (
  request: IModifyClassroomConversationWorkbookRequest,
): Promise<IApiResponse<IClassroomConversationWorkbookData>> => {
  return axios
    .patch<IApiResponse<IClassroomConversationWorkbookData>>(
      `/api/v1/ig/classroom-conversation/workbook/${request.workbookKey}`,
      {
        isStudy: request.isStudy ?? null,
        isBookmark: request.isBookmark ?? null,
      },
    )
    .then((res) => res.data);
};

// ###################################
// problem
// ###################################
// problem 목록 조회 api
export const getProblems = async ({
  page,
  perPage,
  testCode,
}: {
  page: number;
  perPage: number;
  testCode: string | null;
}): Promise<IApiResponse<IProblemData[]>> => {
  let endpoint = `/api/v1/ig/problem?page=${page}&perPage=${perPage}`;

  if (testCode) {
    endpoint += `&testCode=${testCode}`;
  }

  const response = await axios.get<IApiResponse<IProblemData[]>>(endpoint).then((res) => res.data);
  return response;
};

// 오늘의 problem 목록 조회 api
export const getTodayProblems = async ({
  count,
  testCode,
}: {
  count: number;
  testCode: string;
}): Promise<IApiResponse<IProblemData[]>> => {
  const response = await axios
    .get<IApiResponse<IProblemData[]>>(`/api/v1/ig/problem/today?testCode=${testCode}&count=${count}`)
    .then((res) => res.data);
  return response;
};

// problem 수정 api
export const modifyProblem = async (request: IModifyProblemRequest): Promise<IApiResponse<IProblemData>> => {
  const { problemKey, isAttempt } = request;
  const body = { isAttempt };

  const response = await axios
    .patch<IApiResponse<IProblemData>>(`/api/v1/ig/problem/${problemKey}`, body)
    .then((res) => res.data);

  return response;
};

// ###################################
// bundle
// ###################################
// bundle(problem 모음) 목록 조회 api
export const getBundles = async ({
  page,
  perPage,
  bundleCode,
  bundleDate,
}: {
  page: number;
  perPage: number;
  bundleCode: string;
  bundleDate: string;
}): Promise<IApiResponse<IBundleData[]> | void> => {
  if (!bundleCode || !bundleDate) return;
  const endpoint = `/api/v1/ig/bundle?page=${page}&perPage=${perPage}&bundleDate=${bundleDate}&bundleCode=${bundleCode}`;
  const response = await axios.get<IApiResponse<IBundleData[]>>(endpoint).then((res) => res.data);
  return response;
};

// bundle 날짜 목록 조회 api
export const getBundleDates = async ({
  page,
  perPage,
}: {
  page: number;
  perPage: number;
}): Promise<IApiResponse<string[]> | void> => {
  const endpoint = `/api/v1/ig/bundle/date?page=${page}&perPage=${perPage}`;
  const response = await axios.get<IApiResponse<string[]>>(endpoint).then((res) => res.data);
  return response;
};

// ###################################
// student
// ###################################
// student 생성 api
export const createStudent = async (request: ICreateStudentRequest): Promise<IApiResponse<IStudentData>> => {
  const endpoint = `/api/v1/ig/student`;
  const response = await axios.post<IApiResponse<IStudentData>>(endpoint, request).then((res) => res.data);
  return response;
};

// student 단일 조회 api
export const getStudent = async (studentKey: string): Promise<IApiResponse<IStudentData>> => {
  const endpoint = `/api/v1/ig/student/${studentKey}`;
  const response = await axios.get<IApiResponse<IStudentData>>(endpoint).then((res) => res.data);
  return response;
};

// student(me) 단일 조회 api
export const getStudentMe = async (): Promise<IApiResponse<IStudentData>> => {
  const endpoint = `/api/v1/ig/student/me`;
  const response = await axios.get<IApiResponse<IStudentData>>(endpoint).then((res) => res.data);
  return response;
};

// student(studentKey) 단일 수정 api
export const modifyStudent = async (request: IModifyStudentRequest): Promise<IApiResponse<IStudentData>> => {
  const { studentKey, ...body } = request;
  const response = await axios
    .patch<IApiResponse<IStudentData>>(`/api/v1/ig/student/${studentKey}`, body)
    .then((res) => res.data);
  return response;
};

// student(studentKey) 출석 체크 api
export const checkStudentAttendance = async (
  request: ICheckAttendanceRequest,
): Promise<IApiResponse<IAttendanceData>> => {
  const { studentKey, attendedAt, attendanceType } = request;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Asia/Seoul';
  const body = { attendedAt, attendanceType, timeZone, platform: getPlatform() };
  const response = await axios
    .post<IApiResponse<IAttendanceData>>(`/api/v1/ig/student/${studentKey}/attendance`, body)
    .then((res) => res.data);
  return response;
};

// student(studentKey) 학습 기록 누적 api
export const createStudyRecord = async (request: ICreateStudyRecordRequest): Promise<IApiResponse<void>> => {
  const { studentKey, ...body } = request;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Asia/Seoul';
  return axios
    .post<IApiResponse<void>>(`/api/v1/ig/student/${studentKey}/study-record`, { ...body, timeZone })
    .then((res) => res.data);
};

// student(studentKey) 출석 조회 api
export const readAttendancePage = async (
  request: IReadAttendancePageRequest,
): Promise<IApiResponse<IAttendanceData[]>> => {
  const { studentKey, attendanceType, page, perPage } = request;
  const response = await axios
    .get<IApiResponse<IAttendanceData[]>>(`/api/v1/ig/student/${studentKey}/attendance`, {
      params: { attendanceType, page, perPage },
    })
    .then((res) => res.data);
  return response;
};

// 학습기록 조회 api
export const readStudyRecords = async ({
  studentKey,
  languageCode,
  page,
  perPage,
}: {
  studentKey: string;
  languageCode: EApiLanguageCode;
  page: number;
  perPage: number;
}): Promise<IApiResponse<IStudyRecordData[]>> => {
  const response = await axios
    .get<
      IApiResponse<IStudyRecordData[]>
    >(`/api/v1/ig/student/${studentKey}/record?page=${page}&perPage=${perPage}&languageCode=${languageCode}`)
    .then((res) => res.data);
  return response;
};

// ###################################
// vocabulary
// ###################################
// 오늘의 어휘 api - deprecated
export const getTodayVocabularies = async ({
  languageCode,
  count,
}: {
  languageCode: EApiLanguageCode;
  count: number;
}): Promise<IApiResponse<IVocabularyData[]>> => {
  const endpoint = `/api/v1/ig/source-vocabulary/today?languageCode=${languageCode}&count=${count}`;
  const response = await axios.get<IApiResponse<IVocabularyData[]>>(endpoint).then((res) => res.data);
  return response;
};

// ############### workbook ###############
// Worksheet workbook 수정
export const modifyWorksheetWorkbook = async (
  request: IModifyWorksheetWorkbookRequest,
): Promise<IApiResponse<IWorksheetWorkbookData>> => {
  return axios
    .patch<IApiResponse<IWorksheetWorkbookData>>(
      `/api/v1/ig/worksheet/workbook/${request.workbookKey}`,
      {
        studentKey: request.studentKey,
        isStudy: request.isStudy,
      },
    )
    .then((res) => res.data);
};

export const createWorksheetWorkbookUpload = async (
  workbookKey: string, request: { studentKey: string; format: TAudioFormat },
): Promise<IApiResponse<IWorksheetWorkbookUploadData>> => {
  const response = await axios.post<IApiResponse<IWorksheetWorkbookUploadData>>(
    `/api/v1/ig/worksheet/workbook/${workbookKey}/upload`, request,
  );
  return response.data;
};

export const checkWorksheetWorkbookUpload = async (
  workbookKey: string, request: { studentKey: string },
): Promise<IApiResponse<void>> => {
  const response = await axios.post<IApiResponse<void>>(
    `/api/v1/ig/worksheet/workbook/${workbookKey}/upload-check`, request,
  );
  return response.data;
};

export const createWorksheetWorkbookTranscribe = async (
  workbookKey: string, request: { studentKey: string },
): Promise<IApiResponse<IWorksheetWorkbookTranscribeData>> => {
  const response = await axios.post<IApiResponse<IWorksheetWorkbookTranscribeData>>(
    `/api/v1/ig/worksheet/workbook/${workbookKey}/transcribe`, request,
  );
  return response.data;
};

export const getTodayWorksheetWorkbook = async (request: { languageCode: EApiLanguageCode; studentKey: string }): Promise<IApiResponse<IWorksheetWorkbookData>> => {
  return axios.get<IApiResponse<IWorksheetWorkbookData>>('/api/v1/ig/worksheet/workbook/today', { params: request }).then((res) => res.data);
};

export const getTodayClozeWorkbook = async (
  request: IReadTodayClozeWorkbookRequest,
): Promise<IApiResponse<IWorkbookData[]>> => {
  let url = `/api/v1/ig/cloze/workbook/today?languageCode=${request.languageCode}&clozeType=${request.clozeType}`;
  if (request.studentKey) url += `&studentKey=${request.studentKey}`;
  const response = await axios.get<IApiResponse<IWorkbookData[]>>(url).then((res) => res.data);
  return response;
};

// 북마크 workbook(cloze) 조회 api
export const getBookmarkClozeWorkbook = async (
  request: IReadBookmarkClozeWorkbookRequest,
): Promise<IApiResponse<IWorkbookData[]>> => {
  let url = `/api/v1/ig/cloze/workbook/bookmark?languageCode=${request.languageCode}&clozeType=${request.clozeType}&page=${request.page}&perPage=${request.perPage}`;
  if (request.isBookmarkRepeat != null) url += `&isBookmarkRepeat=${request.isBookmarkRepeat}`;

  const response = await axios.get<IApiResponse<IWorkbookData[]>>(url).then((res) => res.data);
  return response;
};

// 오답 workbook(cloze) 조회 api
export const getIncorrectNoteClozeWorkbook = async (
  request: IReadIncorrectNoteClozeWorkbookRequest,
): Promise<IApiResponse<IWorkbookData[]>> => {
  let url = `/api/v1/ig/cloze/workbook/incorrect-note?languageCode=${request.languageCode}&clozeType=${request.clozeType}&page=${request.page}&perPage=${request.perPage}`;
  if (request.isIncorrectRepeat != null) url += `&isIncorrectRepeat=${request.isIncorrectRepeat}`;

  const response = await axios.get<IApiResponse<IWorkbookData[]>>(url).then((res) => res.data);
  return response;
};

// 오늘의 workbook(test) 만들기 api
export const postTodayTestWorkbook = async (
  request: ICreateTodayTestWorkbookRequest,
): Promise<IApiResponse<IWorkbookData[]>> => {
  const response = await axios
    .post<IApiResponse<IWorkbookData[]>>(`/api/v1/ig/test/workbook/today`, {
      testCode: request.testCode,
    })
    .then((res) => res.data);
  return response;
};

// workbook 수정 api
export const modifyWorkbook = async (request: IModifyWorkbookRequest): Promise<IApiResponse<IWorkbookData>> => {
  const response = await axios
    .patch<IApiResponse<IWorkbookData>>(`/api/v1/ig/cloze/workbook/${request.workbookKey}`, {
      isCorrect: request.isCorrect ?? null,
      isBookmark: request.isBookmark ?? null,
      isBookmarkRepeat: request.isBookmarkRepeat ?? null,
      isIncorrectRepeat: request.isIncorrectRepeat ?? null,
      isIncorrectNote: request.isIncorrectNote ?? null,
      studyTime: request.studyTime ?? null,
      reviewAfter: request.reviewAfter ?? null,
    })
    .then((res) => res.data);
  return response;
};

// ###################################
// authentic expression
// ###################################
// 생생표현 목록 조회 api
export const getExpressions = async ({
  page,
  perPage,
  languageCode,
  releaseDate,
}: {
  page: number;
  perPage: number;
  languageCode: EApiLanguageCode;
  releaseDate: string;
}): Promise<IApiResponse<IAuthenticExpressionData[]>> => {
  const endpoint = `/api/v1/ig/expression?languageCode=${languageCode}&page=${page}&perPage=${perPage}&releaseDate=${releaseDate}`;
  const response = await axios.get<IApiResponse<IAuthenticExpressionData[]>>(endpoint).then((res) => res.data);
  return response;
};

// ###################################
// story
// ###################################
// 이야기 목록 조회 api
export const getStories = async ({
  page,
  perPage,
  languageCode,
  storyType,
  releaseDate,
}: {
  page: number;
  perPage: number;
  languageCode: EApiLanguageCode;
  storyType: EApiStoryType;
  releaseDate: string;
}): Promise<IApiResponse<IStoryData[]>> => {
  const endpoint = `/api/v1/ig/story?languageCode=${languageCode}&storyType=${storyType}&page=${page}&perPage=${perPage}&releaseDate=${releaseDate}`;
  const response = await axios.get<IApiResponse<IStoryData[]>>(endpoint).then((res) => res.data);
  return response;
};

// ###################################
// vocabulary book (me)
// ###################################
export const getVocabularyBooks = async (
  request: IReadVocabularyBooksRequest,
): Promise<IApiResponse<IVocabularyBook[]>> => {
  let url = `/api/v1/ig/vocabulary-book?page=${request.page}&perPage=${request.perPage}&languageCode=${request.languageCode}`;
  if (request.category) url += `&category=${request.category}`;
  if (request.levels && request.levels.length > 0) {
    request.levels.forEach((lv) => (url += `&levels=${lv}`));
  }
  if (request.studentKey) url += `&studentKey=${request.studentKey}`;
  const response = await axios.get<IApiResponse<IVocabularyBook[]>>(url).then((res) => res.data);
  return response;
};

export const getVocabularyBookProgress = async (
  request: IReadVocabularyBookProgressRequest,
): Promise<IApiResponse<IVocabularyBookProgress>> => {
  let url = `/api/v1/ig/vocabulary-book/progress?languageCode=${request.languageCode}&category=${request.category}&level=${request.level}`;
  if (request.studentKey) url += `&studentKey=${request.studentKey}`;
  const response = await axios.get<IApiResponse<IVocabularyBookProgress>>(url).then((res) => res.data);
  return response;
};

export const getVocabularyWorkbookBook = async (
  request: IReadVocabularyWorkbookBookRequest,
): Promise<IApiResponse<IVocabularyWorkbookData[]>> => {
  let url = `/api/v1/ig/vocabulary-book/${request.vocabularyBookKey}/workbook`;
  if (request.studentKey) url += `?studentKey=${request.studentKey}`;
  const response = await axios.get<IApiResponse<IVocabularyWorkbookData[]>>(url).then((res) => res.data);
  return response;
};

export const getVocabularyWorkbookBookmarkPage = async (
  request: IReadVocabularyWorkbookBookmarkPageRequest,
): Promise<IApiResponse<IVocabularyWorkbookData[]>> => {
  let url = `/api/v1/ig/vocabulary-book/workbook/bookmark?page=${request.page}&perPage=${request.perPage}&languageCode=${request.languageCode}`;
  if (request.isBookmarkRepeat != null) url += `&isBookmarkRepeat=${request.isBookmarkRepeat}`;
  const response = await axios.get<IApiResponse<IVocabularyWorkbookData[]>>(url).then((res) => res.data);
  return response;
};

export const getVocabularyWorkbookIncorrectNotePage = async (
  request: IReadVocabularyWorkbookIncorrectNotePageRequest,
): Promise<IApiResponse<IVocabularyWorkbookData[]>> => {
  let url = `/api/v1/ig/vocabulary-book/workbook/incorrect-note?page=${request.page}&perPage=${request.perPage}&languageCode=${request.languageCode}`;
  if (request.isIncorrectRepeat != null) url += `&isIncorrectRepeat=${request.isIncorrectRepeat}`;
  const response = await axios.get<IApiResponse<IVocabularyWorkbookData[]>>(url).then((res) => res.data);
  return response;
};

export const getReviewVocabularyWorkbookPage = async (
  request: IReadReviewVocabularyWorkbookPageRequest,
): Promise<IApiResponse<IVocabularyWorkbookData[]>> => {
  const url = `/api/v1/ig/vocabulary-book/workbook/review?page=${request.page}&perPage=${request.perPage}&languageCode=${request.languageCode}&category=${request.category}&level=${request.level}`;
  const response = await axios.get<IApiResponse<IVocabularyWorkbookData[]>>(url).then((res) => res.data);
  return response;
};

export const modifyVocabularyWorkbook = async (
  request: IModifyVocabularyWorkbookRequest,
): Promise<IApiResponse<IVocabularyWorkbookData>> => {
  const response = await axios
    .patch<IApiResponse<IVocabularyWorkbookData>>(`/api/v1/ig/vocabulary-book/workbook/${request.workbookKey}`, {
      isBookmark: request.isBookmark ?? null,
      grade: request.grade ?? null,
      isBookmarkRepeat: request.isBookmarkRepeat ?? null,
      isIncorrectRepeat: request.isIncorrectRepeat ?? null,
      isIncorrectNote: request.isIncorrectNote ?? null,
      reviewAfter: request.reviewAfter ?? null,
    })
    .then((res) => res.data);
  return response;
};

// ############### ai speaking ###############
export const getSpeakingTasks = async ({
  languageCode,
}: {
  languageCode: EApiLanguageCode;
}): Promise<IApiResponse<ISpeakingTaskData[]>> => {
  const endpoint = `/api/v1/ig/speaking/task?languageCode=${languageCode}`;
  return axios.get<IApiResponse<ISpeakingTaskData[]>>(endpoint).then((res) => res.data);
};

export const getSpeakingSessionPage = async ({
  studentKey,
  languageCode,
  page,
  perPage,
}: {
  studentKey: string;
  languageCode: EApiLanguageCode;
  page: number;
  perPage: number;
}): Promise<IApiResponse<ISpeakingSessionData[]>> => {
  const endpoint = `/api/v1/ig/speaking/session?studentKey=${studentKey}&languageCode=${languageCode}&page=${page}&perPage=${perPage}`;
  return axios.get<IApiResponse<ISpeakingSessionData[]>>(endpoint).then((res) => res.data);
};

export const createSpeakingSession = async (
  request: ICreateSpeakingSessionRequest,
): Promise<IApiResponse<ISpeakingSessionData>> => {
  const response = await axios
    .post<IApiResponse<ISpeakingSessionData>>('/api/v1/ig/speaking/session', request)
    .then((res) => res.data);
  return response;
};

export const getSpeakingSession = async (sessionKey: string): Promise<IApiResponse<ISpeakingSessionData>> => {
  return axios
    .get<IApiResponse<ISpeakingSessionData>>(`/api/v1/ig/speaking/session/${sessionKey}`)
    .then((res) => res.data);
};

export const createSpeakingTurn = async (sessionKey: string): Promise<IApiResponse<ISpeakingTurnData>> => {
  const response = await axios
    .post<IApiResponse<ISpeakingTurnData>>(`/api/v1/ig/speaking/session/${sessionKey}/turn`)
    .then((res) => res.data);
  return response;
};

export const removeSpeakingTurn = async (sessionKey: string, turnKey: string): Promise<void> => {
  await axios.delete(`/api/v1/ig/speaking/session/${sessionKey}/turn/${turnKey}`);
};

export const createSpeakingTurnUpload = async (
  sessionKey: string,
  turnKey: string,
  format: TAudioFormat,
): Promise<IApiResponse<ISpeakingTurnUploadData>> => {
  const response = await axios
    .post<
      IApiResponse<ISpeakingTurnUploadData>
    >(`/api/v1/ig/speaking/session/${sessionKey}/turn/${turnKey}/upload`, { format })
    .then((res) => res.data);
  return response;
};

export const createSpeakingTurnTranscribe = async (
  sessionKey: string,
  turnKey: string,
): Promise<IApiResponse<ISpeakingTurnData>> => {
  const response = await axios
    .post<IApiResponse<ISpeakingTurnData>>(`/api/v1/ig/speaking/session/${sessionKey}/turn/${turnKey}/transcribe`)
    .then((res) => res.data);
  return response;
};

export const createSpeakingTurnAnalyze = async (
  sessionKey: string,
  turnKey: string,
): Promise<IApiResponse<ISpeakingTurnData>> => {
  const response = await axios
    .post<IApiResponse<ISpeakingTurnData>>(`/api/v1/ig/speaking/session/${sessionKey}/turn/${turnKey}/analyze`)
    .then((res) => res.data);
  return response;
};

export const createSpeakingTurnRespond = async (
  sessionKey: string,
  turnKey: string,
): Promise<IApiResponse<ISpeakingTurnData | null>> => {
  const response = await axios
    .post<IApiResponse<ISpeakingTurnData | null>>(`/api/v1/ig/speaking/session/${sessionKey}/turn/${turnKey}/respond`)
    .then((res) => res.data);
  return response;
};

export const checkSpeakingTurnUpload = async (
  sessionKey: string,
  turnKey: string,
): Promise<IApiResponse<ISpeakingTurnData>> => {
  const response = await axios
    .post<IApiResponse<ISpeakingTurnData>>(`/api/v1/ig/speaking/session/${sessionKey}/turn/${turnKey}/upload-check`)
    .then((res) => res.data);
  return response;
};

export const modifySpeakingSession = async (sessionKey: string): Promise<IApiResponse<ISpeakingSessionData>> => {
  const response = await axios
    .patch<IApiResponse<ISpeakingSessionData>>(`/api/v1/ig/speaking/session/${sessionKey}`)
    .then((res) => res.data);
  return response;
};

export const readDailyStudyRecords = async (request: {
  studentKey: string; languageCode: EApiLanguageCode; startDate: string; endDate: string;
}): Promise<IApiResponse<IDailyStudyRecordData[]>> => {
  const { studentKey, ...params } = request;
  const response = await axios.get<IApiResponse<IDailyStudyRecordData[]>>(
    `/api/v1/ig/student/${studentKey}/study-record`, { params },
  );
  return response.data;
};
